import { Box, Grid, Paper, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { FC, useEffect, useState } from "react";
import { CopilotAPI } from "../../../api";
import { useGlobalOrganizationContext } from "../../../hooks/useGlobalOrganizationContext";
import { useAlert } from "../../../lib/alert";
import { OpenAIDailyUsage } from "../../../model/CopilotUsage";

const OrganizationCopilotSettings: FC<unknown> = () => {
  const org = useGlobalOrganizationContext().organization;
  const err = useAlert().error;

  const [loaded, setLoaded] = useState(false);
  const [monthly, setMonthly] = useState<number>(0);
  const [usage, setUsage] = useState<OpenAIDailyUsage[]>([]);

  useEffect(() => {
    if (!org?.id) return;

    CopilotAPI.getCurrentUsage(org.id)
      .then((res) => {
        setLoaded(true);
        setUsage(res);

        let total = 0;
        res.forEach((entry) => {
          total += entry.total_tokens || 0;
        });

        setMonthly(total);
      })
      .catch(() => {
        setLoaded(true);
        setUsage([]);
        err("Unable to load current usage.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id]);

  // Prepare data for LineChart: x = date, y = total_tokens
  // Format date as 'Mon DD' (e.g., 'Jul 31')
  function formatDateLabel(dateString: string) {
    const d = new Date(dateString);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  // Only include entries where endpoint is 'autofill'
  const autofillEntries = usage.filter(
    (entry) =>
      entry.endpoint === "autofill" && entry.date && entry.total_tokens != null,
  );

  const chartData = autofillEntries
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((entry) => ({
      x: formatDateLabel(entry.date),
      y: entry.total_tokens || 0,
    }));

  // Calculate total input and output tokens for autofill
  const totalPromptTokens = autofillEntries.reduce(
    (sum, entry) => sum + (entry.total_prompt_tokens || 0),
    0,
  );
  const totalCompletionTokens = autofillEntries.reduce(
    (sum, entry) => sum + (entry.total_completion_tokens || 0),
    0,
  );

  // gpt-4o-mini pricing (per 1M tokens)
  const pricePerInputToken = 0.15 / 1_000_000;
  const pricePerOutputToken = 0.6 / 1_000_000;
  const estimatedCost =
    totalPromptTokens * pricePerInputToken +
    totalCompletionTokens * pricePerOutputToken;

  if (!org) return null;

  return (
    <Grid container p={2}>
      <Grid size={12}>
        <Typography variant="h1" gutterBottom>
          Organization Copilot Management
        </Typography>
        <Typography gutterBottom>
          {`View and monitor your organization's Copilot usage, including daily
          token consumption, input/output token breakdown, and estimated monthly
          costs.`}
        </Typography>
      </Grid>

      {loaded ? (
        <Grid mt={4} size={12}>
          <Paper
            sx={(t) => ({
              borderRadius: 3,
              px: 4,
              pt: 3,
              pb: 1,
              background: t.palette.grey[100],
            })}
          >
            <Typography variant="h3" gutterBottom>
              Copilot Autofill Usage
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Monthly Total Tokens: <b>{monthly}</b>
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Total input tokens: <b>{totalPromptTokens}</b> | Total output
              tokens: <b>{totalCompletionTokens}</b>
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Estimated monthly cost: <b>${estimatedCost.toFixed(4)}</b>
            </Typography>
            <Box mb={3}>
              <LineChart
                xAxis={[
                  {
                    data: chartData.map((d) => d.x),
                    scaleType: "point",
                    label: "Date",
                  },
                ]}
                series={[
                  {
                    data: chartData.map((d) => d.y),
                    label: "Autofill Tokens Used",
                  },
                ]}
                width={500}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
      ) : (
        <Typography>Loading usage data...</Typography>
      )}
    </Grid>
  );
};

export default OrganizationCopilotSettings;
